import { KeyboardEvent, memo, PropsWithChildren } from 'react';
import { Checkbox, Icon, IconButton, IconName, Stack, Tag, Typography } from '@optimus-web/core';
import { useTreeContext } from './context';
import { OptionContent, OptionCounter, OptionSlot, OptionWrapper } from './style';
import { OptionProps } from './useOptions';
import { combineKeydownEvents, handleKeyboardGroupCollapseControl } from './utils';

export const TreeOptionEntry = memo(
    ({
        onToggleExpand,
        expanded,
        checked,
        indeterminate,
        onChange,
        onKeyDown,
        ...props
    }: PropsWithChildren<
        OptionProps & {
            onToggleExpand?: (expand: boolean) => void;
            expanded?: boolean;
            onKeyDown: (e: KeyboardEvent<HTMLLIElement>) => void;
        }
    >) => {
        const { isGroup, label, icon, tagProps, offset = 0, optionsCount, description, id } = props;

        const { size = 'medium', showCount, multiple } = useTreeContext();

        const handleKeyDown =
            isGroup && onToggleExpand
                ? combineKeydownEvents(onKeyDown, handleKeyboardGroupCollapseControl(onToggleExpand))
                : onKeyDown;

        const handleClick = () => {
            if (multiple || !isGroup) {
                onChange(!checked);
            } else if (onToggleExpand) {
                onToggleExpand(!expanded);
            }
        };

        const showStrongText = checked || indeterminate;

        return (
            <OptionWrapper
                tabIndex={0}
                role="option"
                aria-selected={checked}
                onKeyDown={handleKeyDown}
                onClick={handleClick}
                $size={size}
                data-test-tree-option-id={id}
            >
                <OptionContent $offset={offset}>
                    <Stack itemAlignment="center" spacing="100">
                        <OptionSlot>
                            {onToggleExpand ? (
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleExpand(!expanded);
                                    }}
                                    variant="ghost"
                                    size="small"
                                    type="button"
                                    icon={expanded ? IconName.ChevronDown : IconName.ChevronRight}
                                />
                            ) : null}
                        </OptionSlot>

                        {multiple ? (
                            <OptionSlot>
                                <Checkbox
                                    stopClickPropagation
                                    checked={checked}
                                    indeterminate={indeterminate}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(_, c) => onChange(c)}
                                />
                            </OptionSlot>
                        ) : null}

                        {icon ? (
                            <OptionSlot>
                                <Icon size="small" value={icon} />
                            </OptionSlot>
                        ) : null}

                        <Stack vertical itemAlignment="start">
                            <Typography
                                whiteSpace="nowrap"
                                textStyle={showStrongText ? 'bodyMediumStrong' : 'bodyMedium'}
                                as="div"
                            >
                                {label}
                            </Typography>
                            {description ? (
                                <Typography
                                    whiteSpace="nowrap"
                                    textStyle="bodySmall"
                                    variant="tertiary"
                                    ellipsis
                                    as="div"
                                >
                                    {description}
                                </Typography>
                            ) : null}
                        </Stack>

                        {showCount && optionsCount != null ? (
                            <OptionCounter
                                description={!!description}
                                textStyle={showStrongText ? 'bodySmallStrong' : 'bodySmall'}
                                variant="tertiary"
                                as="div"
                            >
                                {optionsCount}
                            </OptionCounter>
                        ) : null}

                        {tagProps ? <Tag variant="basicBold" {...tagProps} /> : null}
                    </Stack>
                </OptionContent>
            </OptionWrapper>
        );
    }
);
